import React, { useEffect, useMemo, useRef, useState } from "react";
import "./BatchCert.scss";
import Buttons from "../../components/ui/button/Buttons";
import Select from "react-select";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
import { GridColumnType } from "../../components/FinalMolGrid/libs/data/gridColumnType";
import { BatchCertificatesConfig } from "./utils/BatchCertificatesConfig";
import { useCreateCertificationInvoiceMutation, useGetAllBatchCertificatesListMutation, useSaveBatchCertificateInvoiceMutation } from "../../app/services/CertificateGrouping/certificateGroupingApi";
import DataLoader from "../../components/ui/dataLoader/DataLoader";
import { encryptAESID } from "../../utils/Encryption";
import { useLazyGetDropDownsForProductsAndServicesQuery } from "../../app/services/productAndService/productAndServiceApi";
import ToastService from "../../services/toastService/ToastService";
import CenterModel from "../../components/ui/centerModel/CenterModel";
import { useAddAuditLogForSGBPApplcation } from "../../utils/AuditLogQuery/addAuditLog";
import SwalAlert from "../../services/swalService/SwalService";

const OVERSEAS_OPTIONS = [
  { value: 1, label: "Yes" },
  { value: 2, label: "No" },
];

const DEFAULT_PAGE_SIZE = 10;

const BatchCert = () => {
  const molGridRef = useRef();
  const gridWrapperRef = useRef();
  const { error } = SwalAlert()
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [search, setSearch] = useState("");
  const [overseasOption, setOverseasOption] = useState(null);
  const [dataSource, setDataSource] = useState();
  const [selectedApprover, setSelectedApprover] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [pushToXero, setPushToXero] = useState(true);
  const [manualInvoiceNumber, setManualInvoiceNumber] = useState("");
  const [manualInvoiceError, setManualInvoiceError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [boardApprovalAssignmentList, { isSuccess: isSuccessBatchList, isLoading: isLoadingBatchList, data: batchListData },] = useGetAllBatchCertificatesListMutation();
  const [productsAndServicesDropdown, { data: productsAndServicesDropDownsData },] = useLazyGetDropDownsForProductsAndServicesQuery();
  const [saveBatchCertificateInvoice, { isSuccess: isSuccessSaveBatchInvoice, isLoading: isLoadingSaveBatchInvoice },] = useSaveBatchCertificateInvoiceMutation();
  const [createCertificationInvoice, { isSuccess: isSuccessCreateCertificationInvoice, isLoading: isLoadingCreateCertificationInvoice },] = useCreateCertificationInvoiceMutation();

  const countryOptionsMemo = useMemo(() => productsAndServicesDropDownsData?.filter((item) => item.dropdownName === "Products Country").map((item) => ({ value: item.id, label: item.name })) ?? [], [productsAndServicesDropDownsData]);
  const approverOptionsMemo = useMemo(() => productsAndServicesDropDownsData?.filter((item) => item.dropdownName === "Approver List").map((item) => ({ value: item.id, label: item.name })) ?? [], [productsAndServicesDropDownsData]);
  const selectedCompanyId = useMemo(() => selectedRows[0]?.sgbcCorporateMemberId ?? null, [selectedRows]);
  const selectedCountryOption = useMemo(() => countryOptionsMemo.find((item) => item.value === selectedRows[0]?.countryId) ?? null, [countryOptionsMemo, selectedRows]);
  const { addAuditLog } = useAddAuditLogForSGBPApplcation();

  const checkboxFieldName = useMemo(() => {
    const checkboxCol = BatchCertificatesConfig?.columns?.find(
      (col) => col.colType === GridColumnType.CHECKBOX
    );
    return checkboxCol?.colSettings?.headerCheckboxField || checkboxCol?.fieldName;
  }, []);

  const selectedIds = useMemo(() => new Set(selectedRows.map((row) => row?.sgbpCertificateGroupingId)),
    [selectedRows]
  );

  const pagedData = useMemo(() => {
    if (!dataSource) return dataSource;
    const start = (currentPage - 1) * pageSize;
    return dataSource.slice(start, start + pageSize).map((row) => checkboxFieldName ? { ...row, [checkboxFieldName]: selectedIds.has(row?.sgbpCertificateGroupingId) } : row
    );
  }, [dataSource, currentPage, pageSize, selectedIds, checkboxFieldName]);

  const handlePageChange = ({ pageNumber, pageSize: newPageSize }) => {
    setCurrentPage(pageNumber);
    setPageSize(newPageSize);
  };

  useEffect(() => {
    if (!isLoadingSaveBatchInvoice && isSuccessSaveBatchInvoice) {
      ToastService.success("Invoice Batch saved successfully.");
      const uniqueApplicationIds = [
        ...new Set(
          selectedRows
            .map((row) => row?.sgbpsApplicationId)
            .filter(Boolean)
        ),
      ];

      for (const applicationId of uniqueApplicationIds) {
        const application = selectedRows.find((row) => row?.sgbpsApplicationId === applicationId);
        addAuditLog({
          SGBPSApplicationId: application?.sgbpsApplicationId,
          AuditLogDescription: "Application Send For Batch Certificate",
          TriggredTimeStatusId: application?.statusId,
        });
      }
      closeAcceptModal();
      setSelectedRows([]);
      setSelectedApprover(null);
      handleSearch(search);
    }
  }, [isLoadingSaveBatchInvoice, isSuccessSaveBatchInvoice]);

  const handleRowSelection = (selectedRow) => {
    const exists = selectedRows?.find((row) => row?.sgbpCertificateGroupingId === selectedRow?.sgbpCertificateGroupingId);
    if (exists) {
      setSelectedRows(selectedRows.filter((row) => row?.sgbpCertificateGroupingId !== selectedRow?.sgbpCertificateGroupingId));
    } else {
      const originalRow = dataSource?.find((row) => row?.sgbpCertificateGroupingId === selectedRow?.sgbpCertificateGroupingId) ?? selectedRow;
      setSelectedRows([...selectedRows, originalRow]);
    }
  };

  const handleGridClickCapture = (e) => {
    const target = e?.target;
    if (target?.type !== "checkbox") return;

    const tr = target?.closest("tr");
    if (!tr || !pagedData) return;

    const tbody = tr?.parentElement;
    const rowIndex = Array.prototype.indexOf.call(tbody.children, tr);
    const clickedRow = pagedData[rowIndex];
    if (!clickedRow) return;

    const isTryingToCheck = target?.checked === true;
    const isDifferentCompany =
      selectedRows.length > 0 && clickedRow?.sgbcCorporateMemberId !== selectedCompanyId;

    if (isTryingToCheck && isDifferentCompany) {
      e?.preventDefault();
      e?.stopPropagation();
      target.checked = false;
      ToastService.warning("You can't select certificates from more than one company at the same time. Please uncheck your current selection first.");
    }
  };

  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper || !pagedData) return;

    const id = requestAnimationFrame(() => {
      const rows = wrapper.querySelectorAll("tbody tr");
      rows.forEach((tr, idx) => {
        const rowData = pagedData[idx];
        const checkbox = tr.querySelector('input[type="checkbox"]');
        if (!rowData || !checkbox) return;
        checkbox.checked = selectedIds.has(rowData?.sgbpCertificateGroupingId);
      });
    });

    return () => cancelAnimationFrame(id);
  }, [pagedData, selectedIds]);

  const handleSearch = (searchValue) => {
    setCurrentPage(1);
    boardApprovalAssignmentList({ CompanyName: searchValue || "" });
  };

  const handleClear = () => {
    setCurrentPage(1);
    boardApprovalAssignmentList({ CompanyName: "" });
    setSearch("");
  };

  useEffect(() => {
    productsAndServicesDropdown();
    handleSearch();
  }, []);

  useEffect(() => {
    if (!selectedCountryOption) {
      setOverseasOption(null);
      return;
    }
    const isSingapore = selectedCountryOption.label === "Singapore";
    setOverseasOption(
      OVERSEAS_OPTIONS.find((opt) => opt.value === (isSingapore ? 2 : 1))
    );
  }, [selectedCountryOption]);

  useEffect(() => {
    if (isSuccessBatchList && !isLoadingBatchList && batchListData) {
      const batchList = batchListData?.map((companyName) => ({
        ...companyName,membershipTaskListLink: `/viewCorporateMember/${encodeURIComponent(encryptAESID(companyName?.sgbcCorporateMemberId))}`,
      }));
      setDataSource(batchList);
      setSelectedRows([]);
    }
  }, [isSuccessBatchList, isLoadingBatchList, batchListData]);

  const openModal = () => {
    if (selectedRows.length === 0) {
      error("Please select at least one certificate to submit.");
      return;
    }
    if (!selectedApprover) {
      error("Please select an Invoice Approver.");
      return;
    }
    setPushToXero(true);
    setManualInvoiceNumber("");
    setManualInvoiceError("");
    setShowAcceptModal(true);
  };

  const closeAcceptModal = () => {
    setShowAcceptModal(false);
    setManualInvoiceNumber("");
    setManualInvoiceError("");
  };

  const handlePushToXeroChange = (value) => {
    setPushToXero(value);
    if (value === true) {
      setManualInvoiceNumber("");
      setManualInvoiceError("");
    }
  };

  const buildInvoicePayload = () => ({
   SGBPSProductInformationId: [...new Set(selectedRows.map((row) => row?.sgbpsApplicationId).filter(Boolean))].join(','), 
    CorporateMemberId: selectedCompanyId,
    ApproverId: selectedApprover?.value,
    CountryId: selectedCountryOption?.value ?? null,
    Overseas: overseasOption?.label === "Yes",
    PushToXero: pushToXero,
    ManualInvoiceNumber: pushToXero ? null : manualInvoiceNumber.trim(),
    CertificateGroupingIds: selectedRows.map((row) => row?.sgbpCertificateGroupingId),
    CertificateNumbersIds: selectedRows.map((row) => row?.certificateNumber),
    ItemCodeIds: selectedRows.map((row) => row?.itemCode),
  });

  const onApproveClick = async () => {
    if (pushToXero === false && !manualInvoiceNumber.trim()) {
      setManualInvoiceError("Manual Invoice Number is required");
      return;
    }

    const payload = buildInvoicePayload();
    try {
      setIsSubmitting(true);
      await saveBatchCertificateInvoice(payload).unwrap();
    } catch (err) {
      ToastService.error("Failed to submit invoice. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingBatchList) {
    return <DataLoader />;
  }

  return (
    <div className="batch-cert-page">
      <div className="header">
        <label className="form-label">Search by Company Name</label>
        <div className="search-bar">
          <input
            type="text"
            value={search}
            placeholder="ABC Tech Pte Ltd"
            className="form-control input-field"
            onChange={(e) => setSearch(e.target.value)}
          />
          <Buttons buttonText="Search" buttonTypeClassName="success-btn" onClick={() => handleSearch(search)} />
          <Buttons buttonText="Clear" buttonTypeClassName="btn black-btn" onClick={handleClear} />
        </div>
      </div>

      <div className="content-section">
        <div className="table-section" ref={gridWrapperRef} onClickCapture={handleGridClickCapture}>
          <FinalMolGrid
            ref={molGridRef}
            configuration={BatchCertificatesConfig}
            dataSource={pagedData}
            allowPagination={true}
            pagination={{
              currentPage: currentPage,
              pageSize: pageSize,
              totalCount: dataSource?.length ?? 0,
            }}
            onPageChange={handlePageChange}
            isLoading={isLoadingBatchList}
            onHandleRowSelect={handleRowSelection}
          />
        </div>

        <div className="form-section">
          <div className="form-group">
            <label>Country</label>
            <input type="text" value={selectedCountryOption?.label ?? ""} disabled />
          </div>

          <div className="form-group">
            <label>Overseas</label>
            <Select
              className="custom-checkbox-select"
              options={OVERSEAS_OPTIONS}
              value={overseasOption}
              onChange={(selected) => setOverseasOption(selected)}
            />
          </div>

          <div className="form-group">
            <label>Invoice Approver <span style={{ color: "red" }}>*</span></label>
            <Select
              className="custom-checkbox-select"
              options={approverOptionsMemo}
              value={selectedApprover}
              onChange={(selected) => setSelectedApprover(selected)}
            />
          </div>

          <Buttons buttonText="Submit" buttonTypeClassName="btn submit-btn" onClick={openModal} />
        </div>
      </div>

      <CenterModel show={showAcceptModal} handleClose={closeAcceptModal} modelTitle="Combine Invoice">
        <label className="form-check-label" htmlFor="flexRadioDefault">
          Push to Xero
        </label>
        <div className="radio-group generate-invoice-model d-flex gap-4 mt-2 mb-4">
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="flexRadioDefault"
              id="flexRadioDefault1"
              checked={pushToXero === true}
              onChange={() => handlePushToXeroChange(true)}
            />
            <label className="form-check-label" htmlFor="flexRadioDefault1">Yes</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="flexRadioDefault"
              id="flexRadioDefault2"
              checked={pushToXero === false}
              onChange={() => handlePushToXeroChange(false)}
            />
            <label className="form-check-label" htmlFor="flexRadioDefault2">No</label>
          </div>
        </div>

        <p>If No, please enter Manual Invoice Number</p>
        <input
          type="text"
          className={`form-control mt-2 ${manualInvoiceError ? "is-invalid" : ""}`}
          value={manualInvoiceNumber}
          disabled={pushToXero === true}
          placeholder={pushToXero ? "Not required" : "Enter manual invoice number"}
          onChange={(e) => {
            setManualInvoiceNumber(e?.target?.value);
            if (e?.target?.value.trim()) setManualInvoiceError("");
          }}
        />
        {manualInvoiceError && <p className="text-danger mt-1 mb-0">{manualInvoiceError}</p>}

        <div className="d-flex justify-content-center gap-2 mt-4">
          <Buttons
            buttonText={isSubmitting ? "Submitting..." : "Submit"}
            buttonTypeClassName="success-btn"
            onClick={onApproveClick}
            isDisable={isSubmitting}
          />
        </div>
      </CenterModel>
    </div>
  );
};

export default BatchCert;